import { ReactNode, useEffect } from 'react';
import {
  EnabledRepositoriesStatus,
  InstallationStatusMessage,
  Step,
} from 'shared/InstallationDialog';
import { useMultiStepDialog } from 'shared/MultiStepDialog';
import { renderTranslations, spawn } from 'utils/translations';
import { Loader } from 'components/Loader';
import {
  INSTALLATION_COMPLETE_TRANSLATION_KEY,
  MARKETPLACE_PHASES,
} from '../consts';
import { useGithubPublicAppInstallation } from '../context';
import { getValidatedInstallationErrorCode } from '../utils';

export const InstallationComplete = () => {
  const spawnStep = spawn(INSTALLATION_COMPLETE_TRANSLATION_KEY);
  const spawnMarketplace = spawn('dialogs.github-mt-installation.marketplace');
  const spawnMarketplaceErrors = spawn(
    'dialogs.github-mt-installation.marketplace.errors',
  );
  const spawnErrors = spawn('dialogs.github-mt-installation.errors');
  const { setIsNextEnabled } = useMultiStepDialog();
  const { marketplacePhase, marketplaceErrorCode } =
    useGithubPublicAppInstallation();
  const installationErrorCode = getValidatedInstallationErrorCode();

  const isMarketplaceLoading = marketplacePhase === MARKETPLACE_PHASES.LOADING;
  const isMarketplaceError = marketplacePhase === MARKETPLACE_PHASES.ERROR;
  const isMarketplaceAlreadyInstalled =
    marketplacePhase === MARKETPLACE_PHASES.ALREADY_INSTALLED;

  useEffect(() => {
    setIsNextEnabled(!isMarketplaceLoading);
  }, [isMarketplaceLoading, setIsNextEnabled]);

  const getTitle = () => {
    if (isMarketplaceLoading) {
      return spawnMarketplace('loading.title');
    }

    if (isMarketplaceError) {
      return spawnMarketplace('error.title');
    }

    if (installationErrorCode) {
      return spawnStep('error.title');
    }

    if (isMarketplaceAlreadyInstalled)
      return spawnMarketplace('already-installed.title');

    return spawnStep('title');
  };

  const getDescription = (): ReactNode => {
    if (isMarketplaceLoading) {
      return spawnMarketplace('loading.description');
    }

    if (isMarketplaceError) {
      return marketplaceErrorCode
        ? spawnMarketplaceErrors(marketplaceErrorCode)
        : spawnMarketplaceErrors('GENERIC');
    }

    if (installationErrorCode) {
      return spawnStep('error.description');
    }

    if (isMarketplaceAlreadyInstalled) {
      return spawnMarketplace('already-installed.description');
    }

    return renderTranslations(
      `${INSTALLATION_COMPLETE_TRANSLATION_KEY}.description`,
    );
  };

  const getContent = () => {
    if (isMarketplaceLoading) {
      return (
        <div className="flex items-center justify-center py-12">
          <Loader />
        </div>
      );
    }

    if (isMarketplaceError) return <></>;

    if (installationErrorCode) {
      return (
        <InstallationStatusMessage
          status="error"
          message={spawnErrors(installationErrorCode)}
        />
      );
    }

    return (
      <EnabledRepositoriesStatus
        title={spawnStep('status-title')}
        description={spawnStep('status-description')}
      />
    );
  };

  return (
    <Step title={getTitle()} description={getDescription()} className="gap-8">
      {getContent()}
    </Step>
  );
};
